import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types'
import api from '../utils/api';
import { toast } from 'react-hot-toast';

const ForgotPasswordForm = ({ onBackToLogin, darkMode }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    
    try {
      // Call your API to send OTP
      const response = await api.post('/auth/forgot-password', {
        email: email.trim()
      });

      // If OTP sent successfully
      if (response.data.success) {
        setOtpSent(true);
        setMessage('OTP has been sent to your email. Redirecting to reset password...');
        
        // Store email in session storage for verification
        sessionStorage.setItem('resetEmail', email);
        
        // Navigate to reset password page immediately
        setTimeout(() => {
          navigate('/reset-password', { 
            state: { email: email },
            replace: true 
          });
        }, 1500);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      <div className="flex flex-col items-center justify-center gap-3">
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          🔑
        </div>
        <h1 className="text-xl font-extrabold text-center ">
          Forgot Password
        </h1>
        <p className="text-sm text-center">
          Enter your email and we&apos;ll send you a link to reset your password
        </p>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {message && (
        <div className={`${otpSent ? 'bg-blue-500/20 border-blue-500' : 'bg-green-500/20 border-green-500'} border text-white px-4 py-3 rounded text-sm`}>
          {message}
          {otpSent && (
            <div className="mt-2">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Redirecting...
            </div>
          )}
        </div>
      )}
      
      {!otpSent && (
        <form onSubmit={handleSubmit} className="mt-2 space-y-6">
          <div className="space-y-4">
            <div className="relative">
              <label htmlFor="email" className="sr-only">Email address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`block w-full pl-10 pr-3 py-3 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  placeholder="Enter your email address"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col space-y-4">
            <motion.button
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 text-sm font-semibold rounded-md text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </motion.button>

            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center justify-center text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors group"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to login
            </button>
          </div>
        </form>
      )}
    </motion.div>
  );
};

ForgotPasswordForm.propTypes = {
  onBackToLogin: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired
};

export default ForgotPasswordForm;